import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import { getActiveAssigneeCount } from "./dragDropAssignment.utils";
import { getPublishItemCounts, getPublishPeriodDetails } from "./eventsWorkspacePublish.utils";

const REPEAT_NEVER = addEditEventsConst.repeatedOptionsValues.never;

/**
 * Whether card Delete should skip the multi-option DeleteEvents page.
 * Matches when those options would all be disabled/useless:
 * non-repeating event/shift with at most one assignee
 * (single staff, or unassigned / no staff in Weekly-by-staff).
 *
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function shouldSkipDeleteOptionsScreen(event) {
  if (!event?.id) return false;

  const repeatedType = Number(event.repeatedType ?? REPEAT_NEVER);
  const isNonRepeat = repeatedType === REPEAT_NEVER;
  const assigneeCount = getActiveAssigneeCount(event);
  const isSingleOrUnassignedStaff = assigneeCount <= 1;

  return isNonRepeat && isSingleOrUnassignedStaff;
}

/**
 * Payload for a simple occurrence delete (DeletionType 1), mirroring the
 * only viable choice on DeleteEvents when options are skipped.
 *
 * @param {{
 *   event: { id: number, userId?: number|null },
 *   requestedDate: string,
 *   lockSelectedUsers?: boolean,
 *   selectedUserIdsFromStaffView?: Array<number|string>,
 * }} params
 */
export function buildSimpleDeleteEventPayload({
  event,
  requestedDate,
  lockSelectedUsers = false,
  selectedUserIdsFromStaffView = [],
}) {
  const lockedUserIds = lockSelectedUsers
    ? (selectedUserIdsFromStaffView?.length
        ? selectedUserIdsFromStaffView.map((id) => Number(id))
        : event?.userId != null
          ? [Number(event.userId)]
          : [])
    : [];

  return {
    SchedulerEventId: event.id,
    UserIds: lockedUserIds,
    IsForAllStaff: lockedUserIds.length === 0,
    DeletionType: 1,
    SelectedDates: [],
    Reason: "",
    DeletedDate: requestedDate,
  };
}

/**
 * @param {Array<{ id?: number, date?: string, userId?: number|null, isUnassigned?: boolean }>} items
 * @param {{ isForAllStaff: boolean }} options
 * @returns {Array<{
 *   IsForAllStaff: boolean,
 *   Items: Array<{
 *     SchedulerEventId: number,
 *     DeletedDate: string,
 *     UserId: number|null,
 *   }>,
 * }>}
 */
export function buildDeleteSchedulerEventsPayloads(
  items = [],
  { isForAllStaff },
) {
  const validItems = (items || []).filter(
    (item) => item?.id != null && item?.date,
  );
  if (!validItems.length) return [];

  // Non staff-name views: always delete for all staff.
  if (isForAllStaff) {
    return [
      {
        IsForAllStaff: true,
        Items: validItems.map((item) => ({
          SchedulerEventId: item.id,
          DeletedDate: item.date,
          UserId: null,
        })),
      },
    ];
  }

  // Staff-name weekly: unassigned cards use IsForAllStaff=true + UserId=null.
  // Assigned staff cards use IsForAllStaff=false + respective UserId.
  const unassignedItems = validItems.filter((item) => item.isUnassigned);
  const staffItems = validItems.filter((item) => !item.isUnassigned);
  const payloads = [];

  if (staffItems.length) {
    payloads.push({
      IsForAllStaff: false,
      Items: staffItems.map((item) => ({
        SchedulerEventId: item.id,
        DeletedDate: item.date,
        UserId: item.userId ?? null,
      })),
    });
  }

  if (unassignedItems.length) {
    payloads.push({
      IsForAllStaff: true,
      Items: unassignedItems.map((item) => ({
        SchedulerEventId: item.id,
        DeletedDate: item.date,
        UserId: null,
      })),
    });
  }

  return payloads;
}

/**
 * Period copy keys for the delete confirmation modal (mirrors publish period layout).
 * @param {string} calendarViewId
 * @param {Date|string} date
 */
export function getDeletePeriodDetails(calendarViewId, date) {
  const publishDetails = getPublishPeriodDetails(calendarViewId, date);
  const introKeyByPublishKey = {
    "scheduler.publishEventShiftIntroDay": "scheduler.deleteEventShiftIntroDay",
    "scheduler.publishEventShiftIntroWeek": "scheduler.deleteEventShiftIntroWeek",
    "scheduler.publishEventShiftIntroMonth":
      "scheduler.deleteEventShiftIntroMonth",
  };
  const periodLabelKeyByPublishKey = {
    "scheduler.publishEventShiftPeriodDate":
      "scheduler.deleteEventShiftPeriodDate",
    "scheduler.publishEventShiftPeriodWeek":
      "scheduler.deleteEventShiftPeriodWeek",
    "scheduler.publishEventShiftPeriodMonth":
      "scheduler.deleteEventShiftPeriodMonth",
  };
  const descriptionKeyByPublishKey = {
    "scheduler.publishEventShiftIntroDay":
      "scheduler.deleteEventShiftDescriptionDay",
    "scheduler.publishEventShiftIntroWeek":
      "scheduler.deleteEventShiftDescriptionWeek",
    "scheduler.publishEventShiftIntroMonth":
      "scheduler.deleteEventShiftDescriptionMonth",
  };

  return {
    introKey: introKeyByPublishKey[publishDetails.introKey],
    periodLabelKey: periodLabelKeyByPublishKey[publishDetails.periodLabelKey],
    descriptionKey: descriptionKeyByPublishKey[publishDetails.introKey],
    periodValue: publishDetails.periodValue,
  };
}

export { getPublishItemCounts as getDeleteItemCounts };
